<!-- 
Dialog for creating or editing a note.
-->
<template>
  <v-dialog :value="show" persistent content-class="dialog lg" >
    <v-form ref="form">
      <v-card>
        <v-toolbar color="secondary" flat dark>
          <v-toolbar-title>
            {{ title }}
            <span class="body-1">{{ subtitle }}</span>
          </v-toolbar-title>
          <v-spacer/>
          <our-user-guide-button topic="levy-collection-providers" section="notes" />
        </v-toolbar>
        <our-busy-indicator :show="busy"/>
        <v-card-text>
          <v-container grid-list-md pa-0>
            <v-layout wrap>
              <v-flex xs12>
                <mavon-editor
                  v-show="show && !loading"
                  :value="note && note.note ? note.note : ''"
                  :editable="canEdit"
                  :toolbars="toolbars"
                  :toolbars-flag="canEdit"
                  :box-shadow="false"
                  :rules="[rules.required]"
                  font-size="12px"
                  language="en"
                  style="height: 306px"
                  @change="update('note', $event)"
                />
              </v-flex>
              <v-flex xs12>
                <p class="mt-2 mb-0  grey--text text--darken-1">
                  You can optionally associate this note with a levy collection and flag it as being a reminder
                  for next year's levy collection of the same type.
                </p>
              </v-flex>
              <v-flex xs6>
                <v-select
                  :value="note.levyCollectionId" 
                  :items="levyCollections"
                  :disabled="!canEdit"
                  label="Levy collection" 
                  @input="update('levyCollectionId', $event)"
                  @keydown.enter.native.prevent
                />
              </v-flex>
              <v-flex xs6>
                <v-switch 
                  :input-value="note.isReminderForNextYear"
                  :disabled="!canEdit"
                  label="Note is a reminder for next year" 
                  color="primary"
                  @change="update('isReminderForNextYear', $event)"
                />
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <template v-if="canEdit">
            <v-btn 
              v-if="noteId" :disabled="busy" flat color="error" 
              @click="confirmDelete(note)"
            >Delete</v-btn>
            <v-spacer/>
            <v-btn :disabled="busy" flat @click.native="hide">Cancel</v-btn>
            <v-btn
              ref="save-button" :disabled="busy" flat color="success"
              type="submit" @click.prevent="save"
            >
              Save
            </v-btn>
          </template>
          <template v-else>
            <v-spacer/>
            <v-btn flat @click.native="hide">Close</v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-form>
    <our-validation-dialog v-model="showValidationDialog" :form="this.$refs.form" :state-error="state.error" />
    <our-confirmation-dialog v-model="showDeleteDialog" title="Delete note" @confirm="deleteNote">
      <p>Are you sure you want to delete this note? This action cannot be undone.</p>
    </our-confirmation-dialog>
  </v-dialog>
</template>

<script>
import _ from "underscore";
import lookupMixins from "@/mixins/lookup.mixins";
import ruleMixins from "@/mixins/rule.mixins";
import mavonMixins from "@/mixins/mavon.mixins";
import * as claim from "@/store/claim-types";
import * as action from "@/store/notes/action-types";
import * as mutation from "@/store/notes/mutation-types";

export default {
  mixins: [lookupMixins, ruleMixins, mavonMixins],
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    noteId: {
      validator: prop => typeof prop === "number" || prop === null,
      default: null
    },
    providerId: {
      validator: prop => typeof prop === "number" || prop === null,
      default: null
    },
    levyCollectionId: {
      validator: prop => typeof prop === "number" || prop === null,
      required: true
    }
  },
  data() {
    return {
      showValidationDialog: false,
      showDeleteDialog: false,
      currentNote: null
    };
  },
  computed: {
    isNew() {
      return this.noteId == null;
    },
    subtitle() {
      return this.loaded ? this.levyCollectionDescription : "";
    },
    note() {
      return this.state.data;
    },
    state() {
      return this.$store.state.notes.edit;
    },
    busy() {
      return this.state.loading || this.state.saving;
    },
    loaded() {
      return this.state.loaded;
    },
    loading() {
      return this.state.loading;
    },
    title() {
      if (this.loading || !this.canEdit) {
        return "Note";
      }
      return this.isNew ? "Add note" : "Edit note";
    },
    canEdit() {
      const hasPermission = this.$store.getters.claims.includes(claim.PROVIDER_NOTES_EDIT);
      return hasPermission;
    },
    levyCollections() {
      var empty = [{ value: null, text: "" }];
      var list = _.map(this.lookups.allLevyCollections, lc => {
        return {
          value: lc.id,
          text: `${lc.year} ${lc.typeDescription}`
        };
      });
      return _.union(empty, list);
    }
  },
  watch: {
    show() {
      this.load();
    },
    loading() {
      if (!this.loading) {
        this.$nextTick(function() {
          var textArea = document.getElementsByClassName("auto-textarea-input")[0];
          textArea.setSelectionRange(0, 0);
          textArea.focus();
        });
      }
    }
  },
  methods: {
    load() {
      if (this.show) {
        if (this.isNew) {
          this.$store.commit(mutation.EDIT_CLEAR);
          this.update("levyCollectionId", this.levyCollectionId);
        } else {
          this.$store.dispatch(action.EDIT_GET, { providerId: this.providerId, noteId: this.noteId });
        }
      }
    },
    update(field, newValue) {
      this.$store.commit(mutation.EDIT_SET_DATA, { [field]: newValue });
    },
    async save() {
      if (
        this.$refs.form.validate() &&
        (await this.$store.dispatch(action.EDIT_SAVE, {
          providerId: this.providerId,
          levyCollectionId: this.levyCollectionId
        }))
      ) {
        this.hide();
      } else {
        this.showValidationDialog = true;
      }
    },
    confirmDelete(note) {
      this.currentNote = note;
      this.showDeleteDialog = true;
    },
    async deleteNote() {
      await this.$store.dispatch(action.EDIT_DELETE, {
        providerId: this.currentNote.providerId,
        levyCollectionId: this.levyCollectionId,
        noteId: this.currentNote.id
      });
      this.showDeleteDialog = false;
      this.hide();
    },
    hide() {
      this.$emit("update:show", false);
    }
  }
};
</script>
